<script lang="ts">
	import { onMount } from 'svelte';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { _ } from 'svelte-i18n';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import type { UploadFileDataResponse } from '$lib/api/restApi';
	import Spinner from '$lib/components/Spinner/Spinner.svelte';
	import { fileUploadStore } from '$lib/components/FileUploader/fileUploadStore';
	import FileUploader from '$lib/components/FileUploader/FileUploader.svelte';
	import { page } from '$app/stores';
	import { NodeViewWrapper } from 'svelte-tiptap';
	import DragAndDropWrapper from '$lib/components/TextEditor/Components/DragAndDropWrapper/DragAndDropWrapper.svelte';
	import type { Editor, NodeViewProps } from '@tiptap/core';
	import type { Asset } from '$lib/api/rpc';
	import { addPreviewImages, removePreviewImages } from '$textEditor/PostComposer/composerStore';

	export let node: NodeViewProps['node'];
	export let deleteNode: () => void;
	export let updateAttributes: NodeViewProps['updateAttributes'];

	if (!$page.data.respHub || !$page.data.restApi)
		throw new Error('TextEditor Gallery: Missing hub or restApi');

	const hubId = $page.data.respHub.hub.id;
	const restApi = $page.data.restApi;

	let images: Array<Asset> = [];

	let loading = true;

	let title: string;
	let description: string;

	export let editor: Editor;

	const deleteNodeWrapper = () => {
		if (images.length > 0) {
			removePreviewImages(images.map((image) => image.oembed?.url) as Array<string>);
		}
		editor.setEditable(true);
		deleteNode();
	};

	const savePreviewImages = (assets: Array<Asset>) => {
		addPreviewImages(assets.map((asset) => asset.oembed?.url) as Array<string>);
	};

	const addImages = async () => {
		const imagesFiles = [...$fileUploadStore];
		$fileUploadStore = [];
		if (imagesFiles.length == 0) return;
		editor.setEditable(false);
		const promises = imagesFiles.map((file) => restApi.uploadFile(hubId, file));
		const newImagesResps = await Promise.all(promises);

		const errorResps = newImagesResps.find((resp) => resp.error);

		if (errorResps) {
			editor.setEditable(true);
			toastMessages.danger('', errorResps.error);
			return;
		}
		const newImages = newImagesResps.map((resp) => resp.data) as UploadFileDataResponse[];

		const newImagesAssets: Array<Asset> = newImages.map((image) => image.asset);

		if (!title && newImagesAssets.length > 0) {
			title = newImagesAssets[0].title;

			updateAttributes({ title });
		}

		if (!description && newImagesAssets.length > 0) {
			description = newImagesAssets[0].description;

			updateAttributes({ description });
		}

		images = [...images, ...newImagesAssets];

		savePreviewImages(newImagesAssets);

		updateAttributes({ images });
		loading = false;
		editor.setEditable(true);
		updateAttributes({ loading });
	};

	const deleteImage = (index: number) => {
		const image = images[index].oembed?.url;
		if (image) removePreviewImages([image]);

		images.splice(index, 1);
		images = [...images];
		if (images.length == 0) deleteNodeWrapper();

		updateAttributes({ images });
	};

	const addImagesLoading = async () => {
		loading = true;
		updateAttributes({ loading });
		await addImages();
	};

	onMount(async () => {
		images = node.attrs.images;
		title = node.attrs.title;
		description = node.attrs.description;
		loading = node.attrs.loading;

		if (!images || images.length == 0) {
			await addImages();
			return;
		}

		savePreviewImages(images);
	});
</script>

<NodeViewWrapper id="svelte-component">
	<DragAndDropWrapper on:delete={deleteNodeWrapper}>
		<div id="container">
			<div id="galleryContainer">
				{#each images as image, i}
					<div class="imageWrapper">
						<div class="image" style:background-image={`url("${image.oembed?.url}")`} />
						<button type="button" class="deleteImage" on:click={() => deleteImage(i)}>
							<Icon icon="delete" size="30" color="grey" clickable />
						</button>
					</div>
				{/each}
				<div id="addImage">
					{#if loading}
						<div><Spinner /></div>
					{:else}
						<FileUploader
							multiple={true}
							accept=".jpg, .jpeg, .gif, .png"
							on:fileUploaded={addImagesLoading}>
							{$_('common.add.image')}
						</FileUploader>
					{/if}
				</div>
			</div>
			<input
				id="title"
				placeholder={$_('composer.placeholder.enter.title')}
				bind:value={title}
				on:change={() => updateAttributes({ title })} />
			<input
				id="description"
				placeholder={$_('composer.placeholder.add.a.description')}
				bind:value={description}
				on:change={() => updateAttributes({ description })} />
		</div>
	</DragAndDropWrapper>
</NodeViewWrapper>

<style>
	#container {
		box-sizing: border-box;
		min-block-size: 120px;
		padding-block-end: 20px;
		overflow: hidden;
		border-radius: 2px;
		background-color: white;
		box-shadow: rgb(0 0 0 / 6%) 0 1px 2px 0;
	}

	#galleryContainer {
		display: flex;
		flex-wrap: wrap;
		padding: 4px;
		background-color: rgb(248 248 248);
		box-shadow: rgb(0 0 0 / 11%) 0 0 0 1px inset;
		gap: 10px;
	}

	.imageWrapper,
	#addImage {
		block-size: 116px;
		inline-size: 116px;
		margin: 5px;
		border-radius: 2px;
	}

	.imageWrapper {
		position: relative;
	}

	.image {
		block-size: 100%;
		inline-size: 100%;
		overflow: hidden;
		border: 1px solid rgb(0 0 0 / 11%);
		opacity: 0.8;
		background-color: rgb(240 240 240);
		background-repeat: no-repeat;
		background-position: 50% 50%;
		background-size: cover;
	}

	.deleteImage {
		all: unset;
		display: none;
		position: absolute;
		inset-block-start: 5px;
		inset-inline-start: 5px;
	}

	.imageWrapper:hover > .deleteImage {
		display: block;
	}

	#addImage {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		background-color: rgb(238 238 238);
		text-align: center;
		cursor: pointer;
	}

	#addImage:hover {
		background-color: rgb(12 142 240);
		color: white;
	}

	#title,
	#description {
		inline-size: 100%;
		border: 0;
	}

	#title {
		padding-block: 7px 2px;
		padding-inline: 0;
		color: rgb(33 41 50);
		font-size: 20px;
	}

	#description {
		display: block;
		max-block-size: 36px;
		margin: 0;
		color: rgb(54 62 69);
		font-size: 1.3rem;
	}

	input {
		padding-inline-start: 10px !important;
	}

	input:focus {
		border: 1px solid var(--blueDark) !important;
		outline: none;
	}
</style>
